import {
  essGet,
  essPost,
  essUpload,
  essPut,
  essGetBlob,
  hrmsGet,
  hrmsPost,
  hrmsPut,
  crmGet,
} from "../api/api";

/**
 * Outstation Travel service.
 */

const CODES = {
  POLICY_SETTINGS_ADD: "HR31A",
  POLICY_SETTINGS_SEARCH: "HR31S",
  POLICY_SETTINGS_UPDATE: "HR31U",

  EXPENSE_HEAD_ADD: "HR32A",
  EXPENSE_HEAD_SEARCH: "HR32S",
  EXPENSE_HEAD_UPDATE: "HR32U",

  TRAVEL_REQUEST_ADD: "ESS08A",
  TRAVEL_REQUEST_SEARCH: "ESS08S",
  TRAVEL_REQUEST_UPDATE: "ESS08U",

  EXPENSE_LINE_ADD: "ESS08A",
  EXPENSE_LINE_SEARCH: "ESS08S",
  EXPENSE_LINE_UPDATE: "ESS08U",
};

const POLICY_SETTINGS_PATH = "/outstation-policy-settings";
const EXPENSE_HEAD_PATH = "/outstation-expense-heads";
const TRAVEL_REQUEST_PATH = "/outstation-expense-claims";
const EXPENSE_LINE_PATH = "/outstation-expense-claims-lines";

function unwrapData(response) {
  return response && response.data !== undefined
    ? response.data
    : response;
}

function pageRows(response) {
  const data = unwrapData(response);

  return (
    data?.content ??
    (Array.isArray(data) ? data : [])
  );
}

/** Full page info from a paged search response (content + totals). */
function pagePayload(response) {
  const data = unwrapData(response);
  const content = data?.content ?? (Array.isArray(data) ? data : []);
  const size = data?.pageSize ?? data?.size ?? (content.length || 10);
  const totalElements = data?.totalElements ?? content.length;
  const totalPages = data?.totalPages ?? Math.max(1, Math.ceil(totalElements / (size || 1)));

  return {
    content,
    pageNumber: data?.pageNumber ?? data?.number ?? 0,
    pageSize: size,
    totalElements,
    totalPages,
    first: data?.first ?? true,
    last: data?.last ?? true,
  };
}

function normalizeTierId(value) {
  if (typeof value === "number") {
    return value;
  }

  const match = String(value ?? "").match(/(1|2|3)/);

  return match ? Number(match[1]) : value;
}

function numeric(value) {
  if (
    value === "" ||
    value === null ||
    value === undefined
  ) {
    return null;
  }

  const result = Number(value);

  return Number.isFinite(result)
    ? result
    : value;
}

function requestPayload(form = {}) {
  return {
    tierId: normalizeTierId(form.tierId),
    dateOfTravel: form.dateOfTravel,
    dateOfReturn: form.dateOfReturn,
    placeOfTravel: form.placeOfTravel,
    modeOfTravelId: numeric(form.modeOfTravelId),
    purpose: form.purpose,
    purposeRecordName: form.purposeRecordName || form.customer || null,
    description: form.description || null,
    advanceRequest: numeric(form.advanceRequest ?? form.advanceRequested ?? 0),
    currencyMetricId: numeric(form.currencyMetricId ?? 1),
    companyArranged:
      form.companyArranged && typeof form.companyArranged === "object"
        ? form.companyArranged
        : (form.companyArrangedExpenseHeads || {}),
  };
}

function expenseHeadPayload(head = {}) {
  return {
    tierId: normalizeTierId(head.tierId),
    headName: head.headName,
    modeName: head.modeName || "NA",
    budgetType: normalizeBudgetType(head.budgetType),
    defaultAmount: numeric(head.defaultAmount ?? 0),
    companyArranged: head.companyArranged !== false,
    isActive: head.isActive !== false,
  };
}

function normalizeBudgetType(value) {
  const text = String(value ?? "");

  if (
    text === "Per day" ||
    text === "Per_Day"
  ) {
    return "Per_Day";
  }

  if (
    text === "At actuals" ||
    text === "At_Actuals"
  ) {
    return "At_Actuals";
  }

  if (text === "Lumpsum") {
    return "Lumpsum";
  }

  return value;
}

function budgetTypeLabel(value) {
  if (value === "Per_Day") return "Per day";
  if (value === "At_Actuals") return "At actuals";
  return value;
}

function normalizeExpenseHead(row = {}) {
  return {
    ...row,
    expenseId: row.expenseId ?? row.headId,
    tierId: normalizeTierId(row.tierId),
    budgetType: budgetTypeLabel(row.budgetType),
    modeName: row.modeName || "NA",
  };
}

function queryString(params = {}) {
  const query = new URLSearchParams();

  Object.entries(params).forEach(
    ([key, value]) => {
      if (
        value !== undefined &&
        value !== null &&
        value !== ""
      ) {
        query.set(
          key,
          String(value)
        );
      }
    }
  );

  const text = query.toString();

  return text
    ? `?${text}`
    : "";
}

/* -------------------------------------------------------------------------- */
/* POLICY SETTINGS                                                            */
/* -------------------------------------------------------------------------- */

/**
 * GET /outstation-policy-settings/search
 */
export async function getTravelPolicySettings(tierId) {
  const response = await hrmsGet(
    `${POLICY_SETTINGS_PATH}/search`,
    CODES.POLICY_SETTINGS_SEARCH
  );

  const rows = pageRows(response);
  const normalizedTier = tierId == null ? null : normalizeTierId(tierId);

  if (normalizedTier != null) {
    const match =
      rows.find((row) => normalizeTierId(row.tierId) === normalizedTier && row.isActive) ??
      rows.find((row) => normalizeTierId(row.tierId) === normalizedTier) ??
      null;
    return match ? { ...match, exists: true } : null;
  }

  return rows.find((row) => row.isActive) ?? rows[0] ?? null;
}

/**
 * POST or PUT /outstation-policy-settings
 */
export async function saveTravelPolicySettings(
  settings = {}
) {
  const body = {
    tierId: normalizeTierId(
      settings.tierId ?? 1
    ),

    tierName:
      settings.tierName ||
      `Tier ${normalizeTierId(
        settings.tierId ?? 1
      )}`,

    approvalFlow:
      settings.approvalFlow ||
      "Manager_Then_Finance",

    isActive:
      settings.isActive !== false,
  };

  const response = settings.policyId || settings.exists
    ? await hrmsPut(
        `${POLICY_SETTINGS_PATH}/update/${body.tierId}`,
        { approvalFlow: body.approvalFlow, isActive: body.isActive },
        CODES.POLICY_SETTINGS_UPDATE
      )
    : await hrmsPost(
        `${POLICY_SETTINGS_PATH}/add`,
        body,
        CODES.POLICY_SETTINGS_ADD
      );

  return unwrapData(response);
}

/* -------------------------------------------------------------------------- */
/* EXPENSE HEADS                                                              */
/* -------------------------------------------------------------------------- */

/**
 * GET /outstation-expense-heads/search/tier/active
 */
export async function getExpensePoliciesByTier(
  tenantId,
  tierId
) {
  const response = await hrmsGet(
    `${EXPENSE_HEAD_PATH}/search/tier/active?tierId=${normalizeTierId(
      tierId
    )}`,
    CODES.EXPENSE_HEAD_SEARCH
  );

  return pageRows(response).map(normalizeExpenseHead);
}

/**
 * POST or PUT /outstation-expense-heads
 */
export async function saveExpensePolicy(
  head = {}
) {
  const body =
    expenseHeadPayload(head);

  const existingId =
    head.expenseId ??
    head.headId;

  const response = existingId
    ? await hrmsPut(
        `${EXPENSE_HEAD_PATH}/update/${existingId}`,
        (({ tierId, ...rest }) => rest)(body),
        CODES.EXPENSE_HEAD_UPDATE
      )
    : await hrmsPost(
        `${EXPENSE_HEAD_PATH}/add`,
        body,
        CODES.EXPENSE_HEAD_ADD
      );

  const saved = unwrapData(response);
  return saved && typeof saved === "object" ? normalizeExpenseHead(saved) : saved;
}

/* -------------------------------------------------------------------------- */
/* TRAVEL REQUESTS                                                            */
/* -------------------------------------------------------------------------- */

/**
 * POST /outstation-expense-claims/add
 *
 * Creates the travel request.
 */
export async function createTravelRequest(
  form
) {
  const response = await essPost(
    `${TRAVEL_REQUEST_PATH}/add`,
    requestPayload(form),
    CODES.TRAVEL_REQUEST_ADD
  );

  return unwrapData(response);
}

/**
 * PUT /outstation-expense-claims/update/{requestId}
 *
 * Updates an existing travel request.
 */
function travelRequestUpdatePayload(form = {}) {
  return {
    tierId: form.tierId == null ? undefined : normalizeTierId(form.tierId),
    dateOfTravel: form.dateOfTravel,
    dateOfReturn: form.dateOfReturn,
    placeOfTravel: form.placeOfTravel,
    modeOfTravelId: numeric(form.modeOfTravelId),
    purpose: form.purpose,
    purposeRecordName: form.purposeRecordName || form.customer || null,
    description: form.description || null,
    advanceRequest: numeric(form.advanceRequest ?? form.advanceRequested ?? 0),
    currencyMetricId: numeric(form.currencyMetricId ?? 1),
    companyArranged:
      form.companyArranged && typeof form.companyArranged === "object"
        ? form.companyArranged
        : (form.companyArrangedExpenseHeads || {}),
    ...(form.status ? { status: form.status } : {}),
    ...(form.rejectReason ? { rejectReason: form.rejectReason } : {}),
    ...(form.sentBackReason ? { sentBackReason: form.sentBackReason } : {}),
    ...(form.cancelReason ? { cancelReason: form.cancelReason } : {}),
  };
}

export async function updateTravelRequest(requestId, form) {
  const response = await essPut(
    `${TRAVEL_REQUEST_PATH}/update/${numeric(requestId)}`,
    travelRequestUpdatePayload(form),
    CODES.TRAVEL_REQUEST_UPDATE
  );

  return unwrapData(response);
}

/**
 * GET /outstation-expense-claims/{requestId}
 *
 * Fetch a single travel request.
 */
export async function getTravelRequestById(
  requestId
) {
  const response = await essGet(
    `${TRAVEL_REQUEST_PATH}/${numeric(
      requestId
    )}`,
    CODES.TRAVEL_REQUEST_SEARCH
  );

  return unwrapData(response);
}

/**
 * GET /outstation-expense-claims/search/{reportType}  (paged)
 * Returns { content, pageNumber, pageSize, totalElements, totalPages, first, last }.
 */
export async function getTravelRequestsPage(filters = {}, reportType = "self") {
  const resolvedType = ["self", "team", "finance"].includes(reportType)
    ? reportType
    : "self";

  const paging = {
    page: filters.page ?? 0,
    size: filters.size ?? 10,
    sortBy: filters.sortBy ?? "travelRequestId",
    sortDir: filters.sortDir ?? "DESC",
  };

  // A status filter uses GET /search/status; no filter keeps the role-based list.
  const url = filters.status
    ? `${TRAVEL_REQUEST_PATH}/search/status${queryString({ status: filters.status, ...paging })}`
    : `${TRAVEL_REQUEST_PATH}/search/${resolvedType}${queryString(paging)}`;

  const response = await essGet(url, CODES.TRAVEL_REQUEST_SEARCH);

  return pagePayload(response);
}

/** Rows of a single page (kept for callers that don't need the totals). */
export async function getTravelRequests(filters = {}, reportType = "self") {
  return (await getTravelRequestsPage(filters, reportType)).content;
}

/** Same Report UI for manager and finance; reportType is role-driven. */
export async function getApprovalTravelRequestsPage(reportType = "team", filters = {}) {
  return getTravelRequestsPage(filters, reportType);
}

export async function getApprovalTravelRequests(reportType = "team", filters = {}) {
  return getTravelRequests(filters, reportType);
}

/**
 * PUT /outstation-expense-claims/submit/{travelRequestId}
 *
 * This is used internally by the single
 * "Submit Request" UI action after creation.
 */
export async function submitTravelRequest(
  requestId
) {
  const response = await essPut(
    `${TRAVEL_REQUEST_PATH}/submit/${numeric(
      requestId
    )}`,
    undefined,
    CODES.TRAVEL_REQUEST_UPDATE
  );

  return unwrapData(response);
}

/**
 * PUT /outstation-expense-claims/submit-expenses/{travelRequestId}
 */
export async function submitTravelExpensesClaim(requestId) {
  const response = await essPut(
    `${TRAVEL_REQUEST_PATH}/submit-expenses/${numeric(requestId)}`,
    undefined,
    CODES.TRAVEL_REQUEST_UPDATE
  );

  return unwrapData(response);
}

/* -------------------------------------------------------------------------- */
/* EXPENSE LINES                                                              */
/* -------------------------------------------------------------------------- */

/**
 * GET /outstation-expense-claims-lines/search/request/active   (default)
 * GET /outstation-expense-claims-lines/search/request          (activeOnly: false)
 *
 * Deleted lines are only made inactive on the server, so by default only the
 * active ones are returned. Pass { activeOnly: false } to include inactive.
 */
export async function getExpenseLines(filters = {}) {
  const travelRequestId = filters.requestId ?? filters.travelRequestId;
  if (!travelRequestId) return [];

  const activeOnly = filters.activeOnly !== false;

  const response = await essGet(
    `${EXPENSE_LINE_PATH}/search/request${activeOnly ? "/active" : ""}${queryString({
      travelRequestId: numeric(travelRequestId),
    })}`,
    CODES.EXPENSE_LINE_SEARCH
  );

  return pageRows(response);
}

/**
 * Download expense document.
 */
export async function downloadExpenseLineDocument(
  expenseId
) {
  return essGetBlob(
    `${EXPENSE_LINE_PATH}/${numeric(
      expenseId
    )}/document`,
    CODES.EXPENSE_LINE_SEARCH
  );
}

/**
 * "Delete" an expense line: updates it with isActive = false (no delete API).
 */
export async function deactivateExpenseLine(line = {}) {
  const lineId = line.expenseLineId ?? line.expenseId ?? line.id;
  return updateExpenseLine(lineId, { ...line, isActive: false });
}

/**
 * Edit an existing expense line (head, date, amount).
 *
 * NOTE: this endpoint is NOT in the OpenAPI spec supplied so far.
 * PUT /outstation-expense-claims-lines/update/{expenseLineId} with a JSON body
 * is assumed - change the call below if the backend exposes it differently
 * (e.g. multipart, or a different path).
 */
export async function updateExpenseLine(expenseLineId, line = {}) {
  const response = await essPut(
    `${EXPENSE_LINE_PATH}/update/${numeric(expenseLineId)}`,
    {
      expenseHeadId: numeric(line.expenseHeadId),
      entryDate: line.entryDate,
      amount: numeric(line.amount ?? 0),
      ...(line.isActive !== undefined ? { isActive: line.isActive } : {}),
    },
    CODES.EXPENSE_LINE_UPDATE
  );

  return unwrapData(response);
}

/**
 * Build multipart FormData for one expense line.
 * Each field is appended separately (no `request` JSON string).
 * `remarks` and `document` are optional and only sent when present.
 * Content-Type is NOT set manually so the browser adds the boundary.
 */
// File -> "37,80,68,70,..." (signed bytes). Spring binds this text to byte[].
const fileToByteString = async (file) =>
  new Int8Array(await file.arrayBuffer()).join(",");

async function expenseLineFormData(requestId, line = {}) {
  const formData = new FormData();

  formData.append(
    "travelRequestId",
    String(numeric(line.travelRequestId ?? requestId))
  );
  formData.append("expenseHeadId", String(numeric(line.expenseHeadId)));
  formData.append("entryDate", line.entryDate);
  formData.append("amount", String(numeric(line.amount ?? 0)));

  const remarks =
    typeof line.remarks === "string" ? line.remarks.trim() : line.remarks;
  if (remarks) {
    formData.append("remarks", remarks);
  }

  if (line.document instanceof File) {
    formData.append("document", await fileToByteString(line.document));
  }

  return formData;
}

/**
 * POST /outstation-expense-claims-lines/add - one multipart request per line.
 */
export async function submitTravelExpenses(
  requestId,
  expenseLines = []
) {
  const results = [];

  for (const line of expenseLines) {
    const response = await essUpload(
      `${EXPENSE_LINE_PATH}/add`,
      await expenseLineFormData(requestId, line),
      CODES.EXPENSE_LINE_ADD
    );

    results.push(unwrapData(response));
  }

  return results;
}

/**
 * GET /saas/crm/customer-info/search/status?accountStatus=Active
 * Returns the company names used in the Customer dropdown of a travel request.
 */
export async function getCustomerNames(accountStatus = "Active") {
  const response = await crmGet(
    `/customer-info/search/status?accountStatus=${encodeURIComponent(
      accountStatus
    )}&page=0&size=1000&sortBy=companyName&sortDir=ASC`,
    "CRM01S"
  );

  const data = unwrapData(response);
  const rows =
    data?.customers ??
    data?.content ??
    (Array.isArray(data) ? data : []);

  return [
    ...new Set(rows.map((c) => c?.companyName).filter(Boolean)),
  ];
}

/* -------------------------------------------------------------------------- */
/* APPROVAL ACTIONS                                                           */
/* -------------------------------------------------------------------------- */

/**
 * PUT /outstation-expense-claims/approve/{travelRequestId}
 *
 * IMPORTANT:
 * Do not send actorRole or nextStatus.
 * The backend determines the acting employee,
 * approval role and next status.
 */
export async function approveTravelRequest(
  requestId
) {
  const response = await essPut(
    `${TRAVEL_REQUEST_PATH}/approve/${numeric(
      requestId
    )}`,
    undefined,
    CODES.TRAVEL_REQUEST_UPDATE
  );

  return unwrapData(response);
}

/**
 * Reject request.
 */
export async function rejectTravelRequest(
  requestId,
  remarks
) {
  const response = await essPut(
    `${TRAVEL_REQUEST_PATH}/update/${numeric(
      requestId
    )}`,
    {
      status: "Rejected",
      rejectReason:
        remarks || null,
    },
    CODES.TRAVEL_REQUEST_UPDATE
  );

  return unwrapData(response);
}

/**
 * Send request back.
 */
export async function sendBackTravelRequest(
  requestId,
  remarks
) {
  const response = await essPut(
    `${TRAVEL_REQUEST_PATH}/update/${numeric(
      requestId
    )}`,
    {
      status: "Sent_Back",
      sentBackReason:
        remarks || null,
    },
    CODES.TRAVEL_REQUEST_UPDATE
  );

  return unwrapData(response);
}

/* -------------------------------------------------------------------------- */
/* DEFAULT EXPORT                                                             */
/* -------------------------------------------------------------------------- */

const outstationTravelService = {
  getTravelPolicySettings,
  saveTravelPolicySettings,

  getExpensePoliciesByTier,
  saveExpensePolicy,

  createTravelRequest,
  updateTravelRequest,
  getTravelRequestById,
  getTravelRequests,
  getApprovalTravelRequests,
  submitTravelExpensesClaim,
  submitTravelRequest,

  getExpenseLines,
  deactivateExpenseLine,
  submitTravelExpenses,
  downloadExpenseLineDocument,

  approveTravelRequest,
  updateExpenseLine,
  rejectTravelRequest,
  sendBackTravelRequest,
};

export default outstationTravelService;